import { Menu, Transition } from '@headlessui/react'
import classNames from 'classnames'
import { NextRouter, useRouter } from 'next/router'
import React, { Fragment, useMemo } from 'react'
import { useCookies } from 'react-cookie'
import { BiGlobeIcon, FeArrowDown, FeArrowUp } from '../../components/common/Icons'
import { GTM_CLICK_EVENT, getEventUserType, sendGTMCustomClickEvent } from '../../helpers/gtmHelper'
import { CurrentUser } from '../../model/Common/common.interface'
import { useChangeLocale } from '../../modules/apis/common/user'
import { useCurrentUser } from '../../modules/context/UserContext'
import useHandleLogout from '../../modules/hooks/student/auth/useHandleLogout'
import LinkLocale from '../../modules/i18n/LinkLocale'
import LoginLinkA from '../../modules/i18n/LoginLinkA'
import { LocaleType } from '../../modules/i18n/config'
import { s_menu } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import { ELEMENT_ID } from '../../modules/ids/e2e/student'
import { STUDENT_BTN_ID } from '../../modules/ids/gtm/student'
import urls from '../../modules/urls'
import { SIGN_UP_MODE } from '../../modules/vars'
import Button from '../_legacy/Button'
import { STUDENT_MATERIAL_SUB_TAB_KEY_DICT } from '../materials/const'

interface IRightMenu {
  languageNavigation: any
  rightNav: any
  onClickLogout?: any
  onClickMenuHandler?: any
}

enum PageType {
  LANDING_MATERIAL_AD = 'Landing Material Ad',
  PORTAL_MATERIAL_AD = 'Portal Material Ad',
  NOT_DEFINED = 'Not Defined',
}

const RightMenu: React.FC<any> = ({ languageNavigation, rightNav, onClickLogout, onClickMenuHandler }: IRightMenu) => {
  const router = useRouter()
  const [cookie] = useCookies(['logged_by_parent'])
  const { currentUser } = useCurrentUser()
  const { t, updateLandingLocale } = useTranslation()
  const { mutate } = useChangeLocale()

  const handleLogoutFunction = useHandleLogout()

  const isOnBoardingProcess =
    router.asPath.includes('onboarding') ||
    router.asPath.includes('sign-up') ||
    router.asPath.includes('auth/new') ||
    router.asPath.includes('/additional-info')

  const isChildOnBoardAndParentLoggedIn =
    router.asPath.includes('/onboarding/child') && cookie.logged_by_parent === 'true'

  const pageType: PageType = useMemo(() => {
    if (router.asPath.includes('landing/contents/material/ad/')) {
      return PageType.LANDING_MATERIAL_AD
    } else if (router.asPath.includes('portal/materials/ad')) {
      return PageType.PORTAL_MATERIAL_AD
    } else {
      return PageType.NOT_DEFINED
    }
  }, [router.asPath])

  const handleOnChangeLocale = (targetLocale: LocaleType) => {
    if (currentUser) {
      mutate({ locale: targetLocale })
    } else {
      updateLandingLocale(targetLocale)
    }
  }

  const handleOnClickSignup = (_router: NextRouter, _currentUser: CurrentUser, _pageType: PageType) => {
    switch (_pageType) {
      case PageType.LANDING_MATERIAL_AD: {
        const type = _router.query.type ?? undefined
        sendGTMCustomClickEvent(
          GTM_CLICK_EVENT.CLICK_CONTENT_AD_SIGN_UP,
          STUDENT_BTN_ID.SIGN_UP,
          '상단 회원가입 하기 버튼 클릭',
          getEventUserType(_currentUser),
          `Landing Material Ad`,
          _router.asPath,
          {
            tab_name: STUDENT_MATERIAL_SUB_TAB_KEY_DICT[Number(type)],
            landing_type: _router.asPath.includes('landing/contents/material/ad/a') ? 'A' : 'B',
          }
        )
        break
      }

      case PageType.PORTAL_MATERIAL_AD: {
        const type = _router.query.type ?? undefined
        sendGTMCustomClickEvent(
          GTM_CLICK_EVENT.CLICK_CONTENT_AD_SIGN_UP,
          STUDENT_BTN_ID.SIGN_UP,
          '상단 회원가입 하기 버튼 클릭',
          getEventUserType(_currentUser),
          `Landing Material Ad`,
          _router.asPath,
          {
            tab_name: STUDENT_MATERIAL_SUB_TAB_KEY_DICT[Number(type)],
            landing_type: 'C',
          }
        )
        break
      }

      case PageType.NOT_DEFINED: {
        sendGTMCustomClickEvent(
          GTM_CLICK_EVENT.CLICK_BUTTON_NEW,
          STUDENT_BTN_ID.SIGN_UP,
          '회원가입',
          getEventUserType(currentUser),
          'Navigation Bar - Right Menu',
          _router.asPath
        )
        break
      }
    }
  }

  return (
    <div className="hidden items-center lg:flex">
      {rightNav
        .filter((e) => !e.none)
        .map((item, i) => (
          <Menu key={i} as="div" className="relative">
            {({ open }) => (
              <>
                {item.href && item.type === '_blank' ? (
                  <Menu.Item>
                    {({ active }) => (
                      <div className="relative mr-20 flex items-center justify-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2">
                        <span
                          onClick={() => onClickMenuHandler && onClickMenuHandler(item.eventId, item.label)}
                          className={classNames(
                            'text-h5 hover:text-purple-500 ',
                            open ? 'text-purple-500' : 'text-gray-700',
                            'relative'
                          )}
                        >
                          <a href={item.href} target={item.type} rel="noreferrer">
                            <div className={classNames(`${item.isEvent && 'text-red-500'} font-medium `)}>
                              {item?.label}
                            </div>
                          </a>
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                ) : (
                  <>
                    <Menu.Button className="relative mr-20 flex items-center justify-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2">
                      <span
                        className={classNames(
                          'text-h5 hover:text-purple-500 ',
                          open ? 'text-purple-500' : 'text-gray-700',
                          'relative'
                        )}
                      >
                        {item.href ? (
                          <LinkLocale href={item.href}>
                            <div
                              onClick={() => onClickMenuHandler && onClickMenuHandler(item.eventId, item.label)}
                              className={classNames(`${item.isEvent && 'text-red-500'} font-medium `)}
                            >
                              {item?.label}
                            </div>
                          </LinkLocale>
                        ) : (
                          <div onClick={() => onClickMenuHandler && onClickMenuHandler(item.eventId, item.label)}>
                            {item?.label}
                          </div>
                        )}
                      </span>
                      <div>
                        {item?.menuArray?.length > 0 &&
                          (open ? (
                            <FeArrowUp
                              size={14}
                              className={classNames('text-purple-500', {
                                'ml-1': !item.isEvent,
                                'ml-12': item.isEvent,
                              })}
                            />
                          ) : (
                            <FeArrowDown
                              size={14}
                              className={classNames('text-gray-700', {
                                'ml-1': !item.isEvent,
                                'ml-12': item.isEvent,
                              })}
                            />
                          ))}
                      </div>
                    </Menu.Button>
                    {/*  */}
                    {/*  */}
                    {/*  */}
                    {item?.menuArray?.length > 0 && (
                      <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items
                          static
                          className="absolute left-0 top-56 mt-2 flex origin-top-right flex-row rounded-md border bg-white p-16 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                        >
                          <div className="w-[100px]">
                            {item.menuArray.map((side, i) =>
                              side.subMenu
                                ?.filter((e) => !e.none)
                                .map((item) => (
                                  <div
                                    key={item.label}
                                    onClick={() => onClickMenuHandler && onClickMenuHandler(item.eventId, item.label)}
                                    className="py-[4px]"
                                  >
                                    <LinkLocale
                                      href={item.href}
                                      className={classNames('text-h5 py-4 text-black hover:text-purple-500')}
                                    >
                                      {item.label}
                                    </LinkLocale>
                                  </div>
                                ))
                            )}
                          </div>
                        </Menu.Items>
                      </Transition>
                    )}
                  </>
                )}
              </>
            )}
          </Menu>
        ))}
      {languageNavigation.length > 0 && pageType === PageType.NOT_DEFINED && (
        <Menu as="div" className="relative">
          {({ open }) => (
            <>
              <div>
                <Menu.Button
                  className={classNames(
                    'mr-20 flex items-center rounded-full hover:text-purple-500 focus:outline-none focus:ring-2 focus:ring-white',
                    { 'text-purple-500': open, 'text-gray-700': !open }
                  )}
                  onClick={() =>
                    onClickMenuHandler && onClickMenuHandler(STUDENT_BTN_ID.CLICK_MENU_CHANGE_LANG, 'Select Language')
                  }
                >
                  <span className="sr-only">select language</span>
                  <BiGlobeIcon
                    size={16}
                    className={classNames('mr-5', { 'text-purple-500': open }, { 'text-gray-700': !open })}
                    style={{ minWidth: 17, color: 'inherit' }}
                  />
                  {open ? (
                    <FeArrowUp size={9} style={{ color: 'inherit' }} />
                  ) : (
                    <FeArrowDown size={9} style={{ color: 'inherit' }} />
                  )}
                </Menu.Button>
              </div>
              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  static
                  className="border-1 absolute top-48 mt-2 origin-top-right rounded-lg border-gray-200 bg-white shadow-md focus:outline-none "
                  style={{ left: '-60px' }}
                >
                  {languageNavigation.map((item, i) => (
                    <Menu.Item key={item.label}>
                      {({ active }) => (
                        <a
                          href={item.href}
                          className={classNames(
                            active ? '' : '',
                            'text-h5 w-180 block cursor-pointer py-9 pl-16 text-black hover:bg-gray-50 hover:text-purple-500',
                            { 'rounded-t-lg': i == 0 },
                            { 'rounded-b-lg': i == languageNavigation.length - 1 }
                          )}
                          onClick={() => handleOnChangeLocale(item.lang)}
                        >
                          {item.label}
                        </a>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
      )}

      {/* 로그인 --> 로그아웃 */}
      <div className="flex flex-shrink-0 flex-row">
        {currentUser ? (
          <li className="inline-block list-none">
            <a className="" onClick={handleLogoutFunction}>
              <Button sm outlined clx="mr-12">
                {t(s_menu.mypage.sign_out)}
              </Button>
            </a>
          </li>
        ) : (
          <li className="inline-block list-none">
            <LinkLocale href={urls.student.auth.signIn} id={ELEMENT_ID.landing_home_signin_button}>
              <Button sm outlined clx="mr-12">
                {t(s_menu.landing.signin)}
              </Button>
            </LinkLocale>
          </li>
        )}
        {/* 내 강의실, 회원가입  */}
        {currentUser ? (
          <LinkLocale href={urls.student.newPortal.home}>
            <Button primary sm lift>
              {t(s_menu.landing.portal)}
            </Button>
          </LinkLocale>
        ) : (
          <LoginLinkA mode={SIGN_UP_MODE.TRIAL} href={urls.student.auth.new} id={ELEMENT_ID.bt_landing_home_signup}>
            <Button primary sm lift onClick={() => handleOnClickSignup(router, currentUser, pageType)}>
              {t(s_menu.landing.signup)}
            </Button>
          </LoginLinkA>
        )}
      </div>
    </div>
  )
}

export default RightMenu
